import type { CreateSupportTicketFormData } from "../supportTicketTypes";

/** API priority labels accepted by POST /pharmacy/support-tickets/create. */
export type SupportTicketApiPriority = "Low" | "Medium" | "High" | "Critical";

const PRIORITY_LABEL_BY_FORM_VALUE: Record<string, SupportTicketApiPriority> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

/**
 * Maps the create-ticket form priority value to the backend enum label.
 *
 * @param priority - Lowercase priority selected in the modal.
 * @returns API priority label, defaulting to Medium when unset.
 */
export function mapSupportTicketFormPriority(
  priority: string,
): SupportTicketApiPriority {
  const normalized = priority.trim().toLowerCase();
  return PRIORITY_LABEL_BY_FORM_VALUE[normalized] ?? "Medium";
}

/**
 * Builds the JSON body for POST /pharmacy/support-tickets/create.
 *
 * @param orgId - Authorized organization id for the current user.
 * @param form - Create-ticket modal form values.
 * @returns Request payload for the create support ticket endpoint.
 */
export function buildCreateSupportTicketPayload(
  orgId: string,
  form: CreateSupportTicketFormData,
) {
  const patientName = form.patientName.trim();
  const patientMrn = form.mrn.trim();

  return {
    org_id: orgId,
    title: form.issueTitle.trim(),
    description: form.description.trim(),
    priority: mapSupportTicketFormPriority(form.priority),
    patient_name: patientName || null,
    patient_mrn: patientMrn || null,
  };
}
